import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  Trash2,
  Plus,
  Save,
  Loader2,
  MessageSquare,
  Edit2,
  Paperclip,
  UserPlus,
  Search,
  Link2,
  FileText,
  ShoppingBag,
  ExternalLink,
  CheckCircle2 as CheckIcon,
} from 'lucide-react';
import { useLeads2Portal } from '../../contexts/Leads2PortalContext';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const TASK_TYPES = [
  { value: 'call', label: 'Chiamata', color: '#6366f1' },
  { value: 'email', label: 'Email', color: '#8b5cf6' },
  { value: 'meeting', label: 'Incontro', color: '#10b981' },
  { value: 'followup', label: 'Follow-up', color: '#f59e0b' },
  { value: 'other', label: 'Altro', color: '#64748b' },
];

const PRIORITIES = [
  { value: 'alta', label: 'Alta', color: '#ef4444' },
  { value: 'media', label: 'Media', color: '#f59e0b' },
  { value: 'bassa', label: 'Bassa', color: '#64748b' },
];

const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit', year:'2-digit' }) : '—';
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';
const toInputDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AdminLead2Detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token: adminToken, role, basePath, currentUserId, Layout } = useLeads2Portal();
  const isAdmin = role === 'admin';
  const { toast } = useToast();

  const [lead, setLead] = useState(null);
  const [stages, setStages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingLead, setEditingLead] = useState(false);
  const [leadDraft, setLeadDraft] = useState({});
  const [newNote, setNewNote] = useState('');
  const [showConvert, setShowConvert] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [docs, setDocs] = useState({ quotes: [], orders: [], email: null });

  const authHeaders = () => ({
    Authorization: `Bearer ${adminToken}`,
    'Content-Type': 'application/json',
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rLead, rStages, rTasks, rActs] = await Promise.all([
        fetch(`${API}/api/admin/leads2/${id}`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/leads2/stages`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/leads2/${id}/tasks`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/leads2/${id}/activities`, { headers: authHeaders() }),
      ]);
      if (rLead.ok) {
        const j = await rLead.json();
        setLead(j.lead);
        setLeadDraft(j.lead);
      }
      if (rStages.ok) setStages((await rStages.json()).stages || []);
      if (rTasks.ok) setTasks((await rTasks.json()).tasks || []);
      if (rActs.ok) setActivities((await rActs.json()).activities || []);
      const rDocs = await fetch(`${API}/api/admin/leads2/${id}/documents`, { headers: authHeaders() });
      if (rDocs.ok) {
        const jd = await rDocs.json();
        setDocs({ quotes: jd.quotes || [], orders: jd.orders || [], email: jd.converted_user_email || null });
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare il lead', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (adminToken && id) loadAll(); }, [adminToken, id]); // eslint-disable-line

  useEffect(() => {
    if (!adminToken) return;
    fetch(`${API}/api/admin/leads2/assignees`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : { assignees: [] })
      .then((j) => setAssignees(j.assignees || []))
      .catch(() => {});
  }, [adminToken]); // eslint-disable-line

  const assignLead = async (assignedTo) => {
    const a = assignees.find((x) => x.id === assignedTo);
    await fetch(`${API}/api/admin/leads2/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ assigned_to: assignedTo || null, assigned_to_name: a ? a.name : null }),
    });
    loadAll();
    toast({ title: 'Aggiornato', description: assignedTo ? `Lead assegnato a ${a?.name || ''}` : 'Assegnazione rimossa' });
  };

  const changeStage = async (stage_id) => {
    await fetch(`${API}/api/admin/leads2/${id}/stage`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ stage_id }),
    });
    loadAll();
  };

  const saveLeadEdit = async () => {
    const patch = {};
    ['full_name','email','phone','company','source','notes','value_estimate'].forEach((k) => {
      if (leadDraft[k] !== lead[k]) patch[k] = leadDraft[k];
    });
    if (Object.keys(patch).length === 0) { setEditingLead(false); return; }
    await fetch(`${API}/api/admin/leads2/${id}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(patch),
    });
    setEditingLead(false);
    loadAll();
    toast({ title: 'Salvato', description: 'Lead aggiornato' });
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await fetch(`${API}/api/admin/leads2/${id}/activities`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ type: 'note', message: newNote.trim() }),
    });
    setNewNote('');
    loadAll();
  };

  const saveTask = async (data, pendingFiles = []) => {
    const method = data.id ? 'PATCH' : 'POST';
    const url = data.id
      ? `${API}/api/admin/leads2/tasks/${data.id}`
      : `${API}/api/admin/leads2/${id}/tasks`;
    // Costruisci payload pulito: rimuovi stringhe vuote (Pydantic rifiuta "" come datetime)
    const payload = {};
    Object.keys(data).forEach((k) => {
      if (k === 'id' || k === 'lead_id' || k === 'attachments') return;
      const v = data[k];
      if (v === '' || v === undefined || v === null) return;
      payload[k] = v;
    });
    // Convert datetime-local → ISO 8601 UTC
    if (payload.due_at && typeof payload.due_at === 'string' && !payload.due_at.endsWith('Z')) {
      try { payload.due_at = new Date(payload.due_at).toISOString(); } catch { delete payload.due_at; }
    }
    if (payload.reminder_at && typeof payload.reminder_at === 'string' && !payload.reminder_at.endsWith('Z')) {
      try { payload.reminder_at = new Date(payload.reminder_at).toISOString(); } catch { delete payload.reminder_at; }
    }
    const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast({ title: 'Errore', description: err.detail ? JSON.stringify(err.detail) : 'Salvataggio task fallito', variant: 'destructive' });
      return;
    }
    const saved = await res.json().catch(() => ({}));
    const taskId = saved?.task?.id || data.id;
    // Carica eventuali allegati messi in coda nel modal
    if (taskId && pendingFiles.length) {
      for (const file of pendingFiles) {
        const fd = new FormData();
        fd.append('file', file);
        // eslint-disable-next-line no-await-in-loop
        await fetch(`${API}/api/admin/leads2/tasks/${taskId}/attachments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${adminToken}` },
          body: fd,
        });
      }
    }
    setShowTaskForm(false); setEditingTask(null);
    loadAll();
    toast({ title: 'Salvato', description: pendingFiles.length ? `Task salvata con ${pendingFiles.length} allegato/i` : 'Task salvata' });
  };

  const toggleTaskStatus = async (t) => {
    await fetch(`${API}/api/admin/leads2/tasks/${t.id}`, {
      method: 'PATCH', headers: authHeaders(),
      body: JSON.stringify({ status: t.status === 'done' ? 'todo' : 'done' }),
    });
    loadAll();
  };

  const deleteTask = async (t) => {
    if (!window.confirm('Cancellare questa task?')) return;
    await fetch(`${API}/api/admin/leads2/tasks/${t.id}`, { method: 'DELETE', headers: authHeaders() });
    loadAll();
  };

  const uploadTaskAttachment = async (taskId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/admin/leads2/tasks/${taskId}/attachments`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${adminToken}` },
      body: fd,
    });
    if (res.ok) {
      loadAll();
      toast({ title: 'Allegato caricato' });
    } else {
      const e = await res.json().catch(() => ({}));
      toast({ title: 'Errore', description: e.detail || 'Upload fallito', variant: 'destructive' });
    }
  };

  const deleteTaskAttachment = async (taskId, attId) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    await fetch(`${API}/api/admin/leads2/tasks/${taskId}/attachments/${attId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${adminToken}` },
    });
    loadAll();
  };

  const createQuoteForLead = () => {
    if (!lead?.converted_user_id) return;
    const email = docs.email || lead.converted_user_email || lead.email || '';
    const params = new URLSearchParams({ new: '1', customer_id: lead.converted_user_id });
    if (email) params.set('customer_email', email);
    navigate(`/admin/quotes?${params.toString()}`);
  };

  const convertLead = async (payload) => {
    const res = await fetch(`${API}/api/admin/leads2/${id}/convert-customer`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
    });
    if (res.ok) {
      const j = await res.json();
      setShowConvert(false);
      loadAll();
      toast({
        title: j.created_new ? 'Cliente creato' : 'Lead collegato al cliente',
        description: j.created_new ? 'Nuovo cliente creato dall\'anagrafica.' : 'Lead associato a un cliente esistente.',
      });
    } else {
      const err = await res.json().catch(() => ({}));
      toast({ title: 'Errore', description: err.detail ? JSON.stringify(err.detail) : 'Conversione fallita', variant: 'destructive' });
    }
  };

  if (loading || !lead) {
    return (
      <Layout>
        <div className="p-8 text-center text-neutral-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto"/>
        </div>
      </Layout>
    );
  }

  const currentStage = stages.find((s) => s.id === lead.stage_id);
  const todoTasks = tasks.filter((t) => t.status !== 'done');
  const doneTasks = tasks.filter((t) => t.status === 'done');

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="lead2-detail-page">
        <button
          onClick={() => navigate(basePath)}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900"
          data-testid="back-to-leads"
        >
          <ArrowLeft className="w-4 h-4"/> Torna alla pipeline
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Lead info */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5 lg:col-span-1 space-y-4" data-testid="lead-info-card">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-neutral-900">{lead.full_name || '—'}</h2>
                {lead.company && (
                  <div className="text-sm text-neutral-500 inline-flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3.5 h-3.5"/> {lead.company}
                  </div>
                )}
              </div>
              <button
                onClick={() => setEditingLead(!editingLead)}
                className="p-1 hover:bg-neutral-100 rounded"
                data-testid="toggle-edit-lead"
              >
                <Edit2 className="w-4 h-4"/>
              </button>
            </div>

            {!editingLead ? (
              <>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-neutral-700">
                    <Mail className="w-4 h-4 text-neutral-400"/>
                    {lead.email ? <a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a> : <span className="text-neutral-400">—</span>}
                  </div>
                  <div className="flex items-center gap-2 text-neutral-700">
                    <Phone className="w-4 h-4 text-neutral-400"/>
                    {lead.phone ? <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a> : <span className="text-neutral-400">—</span>}
                  </div>
                  {lead.source_label || lead.source ? (
                    <div className="text-xs">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100">
                        {lead.source_label || lead.source}
                      </span>
                    </div>
                  ) : null}
                </div>
                {lead.notes && (
                  <div className="pt-3 border-t border-neutral-100">
                    <div className="text-xs text-neutral-500 mb-1">Note</div>
                    <div className="text-sm text-neutral-700 whitespace-pre-wrap">{lead.notes}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3">
                {['full_name','email','phone','company','source'].map((k) => (
                  <div key={k}>
                    <label className="block text-xs text-neutral-500 mb-1 capitalize">{k.replace('_',' ')}</label>
                    <input
                      value={leadDraft[k] || ''}
                      onChange={(e) => setLeadDraft((p) => ({...p, [k]: e.target.value}))}
                      className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Note</label>
                  <textarea
                    value={leadDraft.notes || ''}
                    onChange={(e) => setLeadDraft((p) => ({...p, notes: e.target.value}))}
                    rows={3}
                    className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={saveLeadEdit} className="inline-flex items-center gap-1 px-3 py-1.5 bg-fuchsia-600 text-white rounded text-sm">
                    <Save className="w-3.5 h-3.5"/> Salva
                  </button>
                  <button onClick={() => { setEditingLead(false); setLeadDraft(lead); }} className="px-3 py-1.5 border border-neutral-200 rounded text-sm">
                    Annulla
                  </button>
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-neutral-100">
              <label className="block text-xs text-neutral-500 mb-1">Stage attuale</label>
              <select
                value={lead.stage_id || ''}
                onChange={(e) => changeStage(e.target.value)}
                className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"
                data-testid="change-stage-select"
                style={currentStage ? { borderColor: currentStage.color + '55' } : {}}
              >
                {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>

            <div className="pt-3 border-t border-neutral-100">
              <label className="block text-xs text-neutral-500 mb-1">Gestito da (commerciale)</label>
              {isAdmin ? (
                <select
                  value={lead.assigned_to || ''}
                  onChange={(e) => assignLead(e.target.value)}
                  className="w-full border border-neutral-200 rounded-lg px-2 py-2 text-sm"
                  data-testid="lead-assignee-select"
                >
                  <option value="">— Non assegnato —</option>
                  {assignees.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}{a.role !== 'admin' ? ` (${a.code || 'agente'})` : ' (admin)'}</option>
                  ))}
                </select>
              ) : (
                <div className="space-y-2">
                  {lead.assigned_to_name ? (
                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                      <span className="w-6 h-6 rounded-full bg-fuchsia-600 text-white inline-flex items-center justify-center text-[10px] font-semibold">
                        {(lead.assigned_to_name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('') || '?').toUpperCase()}
                      </span>
                      {lead.assigned_to_name}
                      {lead.assigned_to === currentUserId && <span className="text-[10px] text-emerald-600 font-semibold">(tu)</span>}
                    </div>
                  ) : (
                    <div className="text-sm text-neutral-400 italic">Non assegnato</div>
                  )}
                  {(!lead.assigned_to || lead.assigned_to === currentUserId) && (
                    <button
                      onClick={() => assignLead(lead.assigned_to === currentUserId ? '' : currentUserId)}
                      className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${lead.assigned_to === currentUserId ? 'border border-neutral-200 text-neutral-600 hover:bg-neutral-50' : 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'}`}
                      data-testid="agent-self-assign-btn"
                    >
                      <UserPlus className="w-4 h-4"/> {lead.assigned_to === currentUserId ? 'Rilascia lead' : 'Prendi in carico'}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className={`pt-3 border-t border-neutral-100 ${isAdmin ? '' : 'hidden'}`}>
              {lead.converted_user_id ? (
                <a
                  href={`/admin/customers/${lead.converted_user_id}`}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium hover:bg-emerald-100"
                  data-testid="lead-converted-badge"
                >
                  <CheckIcon className="w-4 h-4"/> Convertito in cliente — apri scheda
                </a>
              ) : (
                <button
                  onClick={() => setShowConvert(true)}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-fuchsia-600 text-white rounded-lg text-sm font-medium hover:bg-fuchsia-700"
                  data-testid="convert-lead-btn"
                >
                  <UserPlus className="w-4 h-4"/> Converti in cliente
                </button>
              )}
            </div>

            <div className="text-[11px] text-neutral-400 pt-3 border-t border-neutral-100">
              Creato: {fmtDateTime(lead.created_at)}<br/>
              Aggiornato: {fmtDateTime(lead.updated_at)}
            </div>
          </div>

          {/* Mattia / WhatsApp info */}
          {lead.source === 'mattia_whatsapp' && lead.custom_fields && (
            <MattiaInfoCard cf={lead.custom_fields} lead={lead} />
          )}

          {/* Documenti collegati (preventivi / ordini) */}
          {isAdmin && (lead.converted_user_id || docs.quotes.length > 0 || docs.orders.length > 0) && (
            <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid="lead-documents-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-neutral-800 flex items-center gap-2"><Link2 className="w-4 h-4 text-fuchsia-600"/> Documenti collegati</h3>
                {lead.converted_user_id && (
                  <button
                    onClick={createQuoteForLead}
                    data-testid="create-quote-for-lead-btn"
                    className="inline-flex items-center gap-1 text-sm bg-fuchsia-600 text-white px-3 py-1.5 rounded-lg hover:bg-fuchsia-700"
                  >
                    <Plus className="w-4 h-4"/> Crea preventivo
                  </button>
                )}
              </div>

              <div className="mb-4">
                <div className="text-[11px] uppercase text-neutral-500 font-semibold mb-1.5 flex items-center gap-1"><FileText className="w-3.5 h-3.5"/> Preventivi ({docs.quotes.length})</div>
                {docs.quotes.length === 0 ? (
                  <p className="text-sm text-neutral-400">Nessun preventivo collegato.</p>
                ) : (
                  <div className="space-y-1.5">
                    {docs.quotes.map((q) => (
                      <a
                        key={q.id}
                        href={`/admin/quotes?quoteId=${q.id}`}
                        data-testid={`linked-quote-${q.id}`}
                        className="flex items-center justify-between gap-2 p-2 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-sm"
                      >
                        <span className="font-medium text-neutral-800 truncate">{q.quote_number || q.id}</span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          {q.total != null && <span className="text-neutral-600">€ {Number(q.total).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>}
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">{q.status}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-neutral-400"/>
                        </span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] uppercase text-neutral-500 font-semibold mb-1.5 flex items-center gap-1"><ShoppingBag className="w-3.5 h-3.5"/> Ordini ({docs.orders.length})</div>
                {docs.orders.length === 0 ? (
                  <p className="text-sm text-neutral-400">Nessun ordine collegato. Gli ordini compaiono qui convertendo un preventivo in ordine.</p>
                ) : (
                  <div className="space-y-1.5">
                    {docs.orders.map((o) => (
                      <a
                        key={o.id}
                        href={`/admin/orders/${o.id}`}
                        data-testid={`linked-order-${o.id}`}
                        className="flex items-center justify-between gap-2 p-2 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-sm"
                      >
                        <span className="font-medium text-neutral-800 truncate">{o.order_number || o.id}</span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          {o.total != null && <span className="text-neutral-600">€ {Number(o.total).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>}
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">{o.status}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-neutral-400"/>
                        </span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tasks */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5 lg:col-span-2" data-testid="lead-tasks-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-neutral-800">Task ({tasks.length})</h3>
              <button
                onClick={() => { setEditingTask(null); setShowTaskForm(true); }}
                className="inline-flex items-center gap-1 text-sm bg-fuchsia-600 text-white px-3 py-1.5 rounded-lg hover:bg-fuchsia-700"
                data-testid="new-task-btn"
              >
                <Plus className="w-4 h-4"/> Nuova task
              </button>
            </div>

            {todoTasks.length === 0 && doneTasks.length === 0 && (
              <div className="text-sm text-neutral-400 py-6 text-center italic">Nessuna task</div>
            )}

            {todoTasks.length > 0 && (
              <div className="space-y-2 mb-4">
                {todoTasks.map((t) => (
                  <TaskRow key={t.id} task={t} onToggle={toggleTaskStatus} onDelete={deleteTask} onEdit={(x) => { setEditingTask(x); setShowTaskForm(true); }} onUploadAttachment={uploadTaskAttachment} onDeleteAttachment={deleteTaskAttachment}/>
                ))}
              </div>
            )}
            {doneTasks.length > 0 && (
              <details>
                <summary className="cursor-pointer text-xs text-neutral-500 mb-2">Completate ({doneTasks.length})</summary>
                <div className="space-y-2">
                  {doneTasks.map((t) => (
                    <TaskRow key={t.id} task={t} onToggle={toggleTaskStatus} onDelete={deleteTask} onEdit={(x) => { setEditingTask(x); setShowTaskForm(true); }} onUploadAttachment={uploadTaskAttachment} onDeleteAttachment={deleteTaskAttachment}/>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>

        {/* Timeline / Activities */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid="lead-timeline-card">
          <h3 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4"/> Timeline attività
          </h3>
          <div className="flex items-center gap-2 mb-4">
            <input
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addNote()}
              placeholder="Aggiungi una nota alla timeline…"
              className="flex-1 border border-neutral-200 rounded px-3 py-2 text-sm"
              data-testid="new-note-input"
            />
            <button onClick={addNote} className="px-3 py-2 text-sm bg-fuchsia-600 text-white rounded hover:bg-fuchsia-700">
              Aggiungi
            </button>
          </div>
          <div className="space-y-3">
            {activities.length === 0 && <div className="text-sm text-neutral-400 italic text-center py-4">Nessuna attività registrata</div>}
            {activities.map((a) => (
              <div key={a.id} className="flex gap-3 pb-3 border-b border-neutral-50 last:border-0">
                <div className="w-2 h-2 rounded-full bg-fuchsia-500 mt-2 flex-shrink-0"/>
                <div className="flex-1">
                  <div className="text-sm text-neutral-800">{a.message}</div>
                  <div className="text-[11px] text-neutral-400 mt-0.5">
                    {a.created_by_name || 'Sistema'} · {fmtDateTime(a.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showTaskForm && (
        <TaskFormModal
          initial={editingTask}
          onClose={() => { setShowTaskForm(false); setEditingTask(null); }}
          onSave={saveTask}
        />
      )}

      {showConvert && isAdmin && (
        <ConvertCustomerModal
          lead={lead}
          adminToken={adminToken}
          onClose={() => setShowConvert(false)}
          onConvert={convertLead}
        />
      )}
    </Layout>
  );
};

const TaskRow = ({ task, onToggle, onDelete, onEdit, onUploadAttachment, onDeleteAttachment }) => {
  const type = TASK_TYPES.find((t) => t.value === task.type) || TASK_TYPES[4];
  const prio = PRIORITIES.find((p) => p.value === (task.priority || 'media')) || PRIORITIES[1];
  const done = task.status === 'done';
  const overdue = task.due_at && new Date(task.due_at) < new Date() && !done;
  const fileRef = React.useRef(null);
  const attachments = task.attachments || [];
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${done ? 'bg-neutral-50 border-neutral-100' : overdue ? 'bg-red-50/50 border-red-100' : 'bg-white border-neutral-200'}`} data-testid={`task-${task.id}`}>
      <button onClick={() => onToggle(task)} className="flex-shrink-0 mt-0.5" data-testid={`task-toggle-${task.id}`}>
        {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500"/> : <Circle className="w-5 h-5 text-neutral-300 hover:text-fuchsia-500"/>}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${done ? 'line-through text-neutral-400' : 'text-neutral-800'}`}>{task.title}</span>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold" style={{backgroundColor: prio.color+'15', color: prio.color}}>{prio.label}</span>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{backgroundColor: type.color+'15', color: type.color}}>{type.label}</span>
          {overdue && <span className="text-[10px] uppercase text-red-500">In ritardo</span>}
        </div>
        {task.description && <div className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap">{task.description}</div>}
        <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
          {task.due_at && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3"/> {fmtDateTime(task.due_at)}
            </span>
          )}
          {task.reminder_at && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3"/> {fmtDateTime(task.reminder_at)}
            </span>
          )}
        </div>
        {attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs bg-neutral-50 border border-neutral-100 rounded px-2 py-1" data-testid={`attachment-${a.id}`}>
                <Paperclip className="w-3 h-3 text-neutral-400 flex-shrink-0"/>
                <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-fuchsia-700 hover:underline truncate flex-1">{a.original_name}</a>
                <span className="text-neutral-400 flex-shrink-0">{fmtBytes(a.size_bytes)}</span>
                <button onClick={() => onDeleteAttachment(task.id, a.id)} className="text-neutral-400 hover:text-red-500 flex-shrink-0" data-testid={`attachment-delete-${a.id}`}>
                  <Trash2 className="w-3 h-3"/>
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2">
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => { if (e.target.files?.[0]) { onUploadAttachment(task.id, e.target.files[0]); e.target.value = ''; } }}
            data-testid={`attachment-input-${task.id}`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1 text-[11px] text-fuchsia-600 hover:text-fuchsia-800"
            data-testid={`attachment-add-${task.id}`}
          >
            <Paperclip className="w-3 h-3"/> Allega file
          </button>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => onEdit(task)} className="p-1 text-neutral-400 hover:text-neutral-700"><Edit2 className="w-3.5 h-3.5"/></button>
        <button onClick={() => onDelete(task)} className="p-1 text-neutral-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5"/></button>
      </div>
    </div>
  );
};

const TaskFormModal = ({ initial, onClose, onSave }) => {
  const [f, setF] = useState({ type: 'call', priority: 'media', ...initial, due_at: toInputDateTime(initial?.due_at), reminder_at: toInputDateTime(initial?.reminder_at) });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef(null);
  const set = (k, v) => setF((p) => ({...p, [k]: v}));
  const existingAttachments = initial?.attachments || [];

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const tooBig = arr.find((x) => x.size > 15 * 1024 * 1024);
    if (tooBig) { alert(`"${tooBig.name}" supera i 15MB`); return; }
    setFiles((p) => [...p, ...arr]);
  };

  const handleSave = async () => {
    if (!f.title) return;
    setSaving(true);
    await onSave(f, files);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="task-form-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h3 className="font-semibold text-lg">{initial?.id ? 'Modifica task' : 'Nuova task'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Titolo *</label>
            <input value={f.title || ''} onChange={(e) => set('title', e.target.value)} className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm" data-testid="task-title-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Tipo</label>
              <select value={f.type} onChange={(e) => set('type', e.target.value)} className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm">
                {TASK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Priorità</label>
              <select value={f.priority} onChange={(e) => set('priority', e.target.value)} className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm" data-testid="task-priority-select">
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Descrizione</label>
            <textarea value={f.description || ''} onChange={(e) => set('description', e.target.value)} rows={3} className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Scadenza</label>
              <input type="datetime-local" value={f.due_at || ''} onChange={(e) => set('due_at', e.target.value)} className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"/>
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Reminder</label>
              <input type="datetime-local" value={f.reminder_at || ''} onChange={(e) => set('reminder_at', e.target.value)} className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"/>
            </div>
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Note</label>
            <textarea value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"/>
          </div>

          {/* Allegati */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Allegati</label>
            {existingAttachments.length > 0 && (
              <div className="space-y-1 mb-2">
                {existingAttachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-xs bg-neutral-50 border border-neutral-100 rounded px-2 py-1">
                    <Paperclip className="w-3 h-3 text-neutral-400 flex-shrink-0"/>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-fuchsia-700 hover:underline truncate flex-1">{a.original_name}</a>
                    <span className="text-neutral-400 flex-shrink-0">{fmtBytes(a.size_bytes)}</span>
                  </div>
                ))}
              </div>
            )}
            {files.length > 0 && (
              <div className="space-y-1 mb-2">
                {files.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs bg-fuchsia-50 border border-fuchsia-100 rounded px-2 py-1" data-testid={`task-pending-file-${idx}`}>
                    <Paperclip className="w-3 h-3 text-fuchsia-400 flex-shrink-0"/>
                    <span className="truncate flex-1 text-neutral-700">{file.name}</span>
                    <span className="text-neutral-400 flex-shrink-0">{fmtBytes(file.size)}</span>
                    <button onClick={() => setFiles((p) => p.filter((_, i) => i !== idx))} className="text-neutral-400 hover:text-red-500 flex-shrink-0">×</button>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              data-testid="task-attachment-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 text-xs text-fuchsia-600 hover:text-fuchsia-800 border border-dashed border-fuchsia-300 rounded-lg px-3 py-2 w-full justify-center"
              data-testid="task-add-attachment-btn"
            >
              <Paperclip className="w-3.5 h-3.5"/> Aggiungi allegati (max 15MB l'uno)
            </button>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-neutral-100">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-lg">Annulla</button>
          <button onClick={handleSave} disabled={saving || !f.title} data-testid="task-save-btn" className="px-4 py-2 text-sm bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin"/>} Salva
          </button>
        </div>
      </div>
    </div>
  );
};

const ConvertCustomerModal = ({ lead, adminToken, onClose, onConvert }) => {
  const [mode, setMode] = useState('new');
  const fullName = (lead.full_name || '').trim();
  const [form, setForm] = useState({
    firstName: fullName.split(' ')[0] || '',
    lastName: fullName.split(' ').slice(1).join(' ') || '',
    email: lead.email || '',
    phone: lead.phone || '',
    userType: lead.company ? 'business' : 'private',
    companyName: lead.company || '',
    vatNumber: '',
    fiscalCode: '',
    pec: '',
    codiceUnivoco: '',
    address: '',
    city: '',
    province: '',
    zipCode: '',
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // existing search
  const [query, setQuery] = useState(lead.email || lead.full_name || '');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/auth/admin/users?search=${encodeURIComponent(query.trim())}&limit=10`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        const j = await res.json();
        setResults(j.data || []);
      }
    } finally {
      setSearching(false);
    }
  };

  const submit = () => {
    if (mode === 'existing') {
      if (!selected) return;
      onConvert({ mode: 'existing', user_id: selected.id });
    } else {
      if (!form.email.trim()) return;
      onConvert({ mode: 'new', ...form });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="convert-customer-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h3 className="font-semibold text-lg flex items-center gap-2"><UserPlus className="w-5 h-5 text-fuchsia-600"/> Converti in cliente</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded">×</button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-2 p-5 pb-0">
          <button
            onClick={() => setMode('new')}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${mode === 'new' ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white text-neutral-600 border-neutral-200'}`}
            data-testid="convert-mode-new"
          >
            <UserPlus className="w-4 h-4"/> Nuovo cliente
          </button>
          <button
            onClick={() => setMode('existing')}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${mode === 'existing' ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white text-neutral-600 border-neutral-200'}`}
            data-testid="convert-mode-existing"
          >
            <Link2 className="w-4 h-4"/> Associa esistente
          </button>
        </div>

        {mode === 'new' ? (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" checked={form.userType === 'business'} onChange={() => set('userType', 'business')} data-testid="convert-type-business"/> Azienda
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" checked={form.userType === 'private'} onChange={() => set('userType', 'private')} data-testid="convert-type-private"/> Privato
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome *" value={form.firstName} onChange={(v) => set('firstName', v)} testid="convert-firstName"/>
              <Field label="Cognome" value={form.lastName} onChange={(v) => set('lastName', v)} testid="convert-lastName"/>
              <Field label="Email *" value={form.email} onChange={(v) => set('email', v)} testid="convert-email"/>
              <Field label="Telefono" value={form.phone} onChange={(v) => set('phone', v)} testid="convert-phone"/>
            </div>
            {form.userType === 'business' && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-neutral-100">
                <Field label="Ragione sociale" value={form.companyName} onChange={(v) => set('companyName', v)} testid="convert-company"/>
                <Field label="P. IVA" value={form.vatNumber} onChange={(v) => set('vatNumber', v)}/>
                <Field label="Codice Fiscale" value={form.fiscalCode} onChange={(v) => set('fiscalCode', v)}/>
                <Field label="PEC" value={form.pec} onChange={(v) => set('pec', v)}/>
                <Field label="Codice SDI" value={form.codiceUnivoco} onChange={(v) => set('codiceUnivoco', v)}/>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-neutral-100">
              <Field label="Indirizzo" value={form.address} onChange={(v) => set('address', v)}/>
              <Field label="Città" value={form.city} onChange={(v) => set('city', v)}/>
              <Field label="Provincia" value={form.province} onChange={(v) => set('province', v)}/>
              <Field label="CAP" value={form.zipCode} onChange={(v) => set('zipCode', v)}/>
            </div>
            <p className="text-xs text-neutral-400">Se esiste già un cliente con questa email, verrà collegato automaticamente invece di crearne uno nuovo.</p>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && search()}
                  placeholder="Cerca per nome, email, azienda o telefono…"
                  className="w-full border border-neutral-200 rounded-lg pl-9 pr-3 py-2 text-sm"
                  data-testid="convert-search-input"
                />
              </div>
              <button onClick={search} className="px-3 py-2 bg-neutral-800 text-white rounded-lg text-sm" data-testid="convert-search-btn">Cerca</button>
            </div>
            {searching && <div className="text-sm text-neutral-400 text-center py-3"><Loader2 className="w-4 h-4 animate-spin inline"/></div>}
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {results.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setSelected(u)}
                  className={`w-full text-left p-3 rounded-lg border ${selected?.id === u.id ? 'border-fuchsia-500 bg-fuchsia-50' : 'border-neutral-200 hover:bg-neutral-50'}`}
                  data-testid={`convert-user-${u.id}`}
                >
                  <div className="text-sm font-medium text-neutral-800">
                    {u.firstName} {u.lastName}
                    {u.businessInfo?.companyName && <span className="text-neutral-500"> · {u.businessInfo.companyName}</span>}
                  </div>
                  <div className="text-xs text-neutral-500">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                </button>
              ))}
              {!searching && results.length === 0 && (
                <div className="text-sm text-neutral-400 text-center py-4 italic">Nessun risultato. Premi "Cerca".</div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 p-5 border-t border-neutral-100">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-lg">Annulla</button>
          <button
            onClick={submit}
            disabled={mode === 'existing' ? !selected : !form.email.trim()}
            className="px-4 py-2 text-sm bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 disabled:opacity-40"
            data-testid="convert-submit-btn"
          >
            {mode === 'existing' ? 'Collega cliente' : 'Crea cliente'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs text-neutral-500 mb-1">{label}</label>
    <input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm"
      data-testid={testid}
    />
  </div>
);

const fmtRome = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

const MattiaInfoCard = ({ cf, lead }) => {
  const Row = ({ label, value }) => {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'object' && Object.keys(value).length === 0) return null;
    const display = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return (
      <div className="flex gap-2 text-sm">
        <span className="text-neutral-500 w-40 flex-shrink-0">{label}</span>
        <span className="text-neutral-800 break-words">{display}</span>
      </div>
    );
  };
  return (
    <div className="bg-white rounded-2xl border border-fuchsia-200 p-5 lg:col-span-2" data-testid="mattia-info-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100 font-semibold">Mattia / WhatsApp</span>
        {cf.operator_active && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Operatore attivo</span>}
        {cf.human_review_required && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100">Revisione richiesta</span>}
      </div>
      {cf.next_action && (
        <div className="mb-3 p-3 rounded-lg bg-fuchsia-50/60 border border-fuchsia-100">
          <div className="text-[11px] uppercase text-fuchsia-600 font-semibold mb-0.5">Prossima azione</div>
          <div className="text-sm text-neutral-800">{cf.next_action}</div>
        </div>
      )}
      {cf.crm_chat_summary && (
        <div className="mb-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200" data-testid="mattia-chat-summary">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase text-neutral-500 font-semibold">Riepilogo conversazione</div>
            {cf.crm_summary_updated_at && (
              <span className="text-[10px] text-neutral-400">agg. {fmtRome(cf.crm_summary_updated_at)}</span>
            )}
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {String(cf.crm_chat_summary).split(' | ').map((part, i) => {
              const m = part.match(/^(Cliente|Mattia):\s*(.*)$/s);
              if (m) {
                return (
                  <div key={i} className="text-sm text-neutral-700">
                    <span className={`font-semibold ${m[1] === 'Mattia' ? 'text-fuchsia-700' : 'text-indigo-700'}`}>{m[1]}:</span> {m[2]}
                  </div>
                );
              }
              return <div key={i} className="text-sm text-neutral-600 italic">{part}</div>;
            })}
          </div>
        </div>
      )}
      <div className="space-y-1.5">
        <Row label="Prodotto" value={cf.product_type} />
        <Row label="Prodotto (nome)" value={cf.product_name} />
        <Row label="Quantità" value={cf.quantity_interest || cf.quantity_numeric} />
        <Row label="Formato bottiglia" value={cf.bottle_format} />
        <Row label="Uso" value={cf.use_case} />
        <Row label="Profilo gusto" value={cf.flavor_direction} />
        <Row label="Stile etichetta" value={cf.label_style} />
        <Row label="Riferimento comm." value={cf.commercial_reference} />
        <Row label="Finestra contatto" value={cf.preferred_contact_window} />
        <Row label="Callback richiesto" value={cf.callback_request} />
        <Row label="Callback registrato" value={cf.callback_registered} />
        <Row label="Motivo handoff" value={cf.handoff_reason} />
        <Row label="Motivo revisione" value={cf.human_review_reason} />
      </div>
      <div className="mt-3 pt-3 border-t border-neutral-100 space-y-1.5">
        {cf.last_customer_message && (
          <div className="text-sm">
            <div className="text-[11px] text-neutral-500">Ultimo msg cliente · {fmtRome(cf.last_customer_message_at)}</div>
            <div className="text-neutral-800 italic">“{cf.last_customer_message}”</div>
          </div>
        )}
        {cf.last_mattia_message && (
          <div className="text-sm">
            <div className="text-[11px] text-neutral-500">Ultimo msg Mattia · {fmtRome(cf.last_mattia_message_at)}</div>
            <div className="text-neutral-800 italic">“{cf.last_mattia_message}”</div>
          </div>
        )}
      </div>
      <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center justify-between text-[11px] text-neutral-400">
        <span>ID esterno: {lead.external_lead_id?.slice(0, 8)}…</span>
        <span>Ultima sync: {fmtRome(cf.sync_updated_at)}</span>
      </div>
    </div>
  );
};

export default AdminLead2Detail;
